import { useEffect, useRef, useState, type ReactNode } from "react";

import type { CoverSource } from "../../lib/place-photos";

// A cover that cannot end up as a broken image.
//
// It is given the candidates in order (the club's own photo or a licensed one
// of its surroundings, then Google's) and shows the first. If that one fails to load it moves to the next, and when
// there is none left it renders `fallback`, which is the drawn plate. Until
// 21 Sep 2026 a photo that answered 404 simply stayed a broken <img>: the
// comment in the proxy said the page "draws the plate instead", but nothing
// on the page was listening for the error.
//
// The effect covers the case React cannot see: an image that already failed
// before hydration attached the onError handler. Such an image reports
// complete with a natural width of zero.
export function CoverImage({
  sources,
  alt,
  altByKind,
  fallback,
  credit,
  width,
  height,
  srcSet,
  sizes,
  loading = "lazy",
  className,
}: {
  sources: CoverSource[];
  alt: string;
  /**
   * Alt text per kind, overriding `alt`. A licensed photo shows the beach
   * near the club, not the club, and its alt text must not claim otherwise.
   */
  altByKind?: Partial<Record<CoverSource["kind"], string>>;
  fallback: ReactNode;
  /**
   * Small label over the picture: the credit an attribution licence requires,
   * and for a licensed photo the "beach near ..." note. Shown for own and
   * licensed photos only; a Google photo's attribution is on the club page.
   */
  credit?: string | null;
  width?: number;
  height?: number;
  /**
   * Only for the first source (a stored coast photo has 800/1600/2400
   * renderings); a fallback that follows is shown at its plain src.
   */
  srcSet?: string | null;
  sizes?: string;
  /** "eager" for a hero above the fold; cards stay lazy. */
  loading?: "lazy" | "eager";
  className?: string;
}) {
  const [index, setIndex] = useState(0);
  const ref = useRef<HTMLImageElement | null>(null);
  const current = sources[index];

  useEffect(() => {
    const img = ref.current;
    if (img && img.complete && img.naturalWidth === 0 && img.currentSrc) setIndex((i) => i + 1);
  }, [index]);

  if (!current) return <>{fallback}</>;

  return (
    <>
      <img
        ref={ref}
        key={current.src}
        src={current.src}
        alt={altByKind?.[current.kind] ?? alt}
        width={width}
        height={height}
        srcSet={index === 0 && srcSet ? srcSet : undefined}
        sizes={index === 0 && srcSet ? sizes : undefined}
        loading={loading}
        onError={() => setIndex((i) => i + 1)}
        className={className}
      />
      {credit && current.kind !== "google" ? (
        <span className="li-mono pointer-events-none absolute bottom-2 left-2 max-w-[85%] truncate rounded bg-[#1C2430]/60 px-1.5 py-0.5 text-[9px] text-[#F2EFE9]">
          {credit}
        </span>
      ) : null}
    </>
  );
}
